import { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import { Link } from "react-router-dom";
import {
  ArrowLeftIcon,
  UserCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  TagIcon,
  CalendarIcon,
} from "@heroicons/react/outline";
import CustomerServices from "../../services/CustomerServices";
import { HollowDotsSpinner } from "react-epic-spinners";

const serviceStyles = {
  NIN: "bg-rose-100 text-rose-700",
  BVN: "bg-blue-100 text-blue-700",
  FLEXCUBE: "bg-amber-100 text-amber-700",
};

const statusStyles = {
  None: "bg-gray-100 text-gray-700",
  Pending: "bg-amber-100 text-amber-700",
  Initiated: "bg-blue-100 text-blue-700",
  Completed: "bg-green-100 text-green-700",
  Failed: "bg-red-100 text-red-700",
};

export default function ViewOnboardingStatusHistory() {
  const id = new URLSearchParams(window.location.search).get("id");
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchRecord = async () => {
    setLoading(true);
    try {
      const resp = await CustomerServices.viewOnboardingStatusHistory(id);
      if (resp && resp.data) {
        setRecord(resp.data);
      } else {
        setError(resp?.message || "Onboarding record not found");
      }
    } catch (err) {
      setError("Failed to fetch onboarding record details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const SectionCard = ({ title, children, className = "" }) => (
    <div
      className={`bg-white shadow-sm border border-gray-200 rounded-lg ${className}`}
    >
      <div className="px-6 py-5 border-b border-gray-200">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );

  const DetailRow = ({ label, value }) => (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
        {value || "N/A"}
      </dd>
    </div>
  );

  if (loading) {
    return (
      <Layout
        title="Loading..."
        pageTag="customer"
        current="/onboarding-status-history"
      >
        <div className="flex justify-center items-center h-64">
          <HollowDotsSpinner color="#4A90E2" />
        </div>
      </Layout>
    );
  }

  if (error || !record) {
    return (
      <Layout
        title="Onboarding Record Not Found"
        pageTag="customer"
        current="/onboarding-status-history"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <UserCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Onboarding Record Not Found
            </h3>
            <p className="text-gray-500 mb-4">
              {error || "The requested onboarding record could not be found."}
            </p>
            <Link
              to="/onboarding-status-history"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Onboarding Status History
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const service = String(record.serviceAffected || "").toUpperCase();
  const status = record.adminStatus || "None";

  return (
    <Layout
      title={`${record.userName || "Onboarding Record"} - Details`}
      pageTag="customer"
      current="/onboarding-status-history"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link
          to="/onboarding-status-history"
          className="inline-flex items-center text-sm text-red-600 hover:text-red-500 mb-6"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Onboarding Status History
        </Link>

        <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-red-600 to-red-800 px-8 py-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="h-20 w-20 rounded-full bg-white/20 flex items-center justify-center">
                  <UserCircleIcon className="h-12 w-12 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">
                    {record.userName || "—"}
                  </h1>
                  <p className="text-red-100 mt-1">
                    {record.uniqueIdentifier || record.phoneNumber}
                  </p>
                  <div className="flex items-center space-x-4 mt-3">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        serviceStyles[service] || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {record.serviceAffected || "N/A"}
                    </span>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        statusStyles[status] || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {record.adminStatus || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <SectionCard title="Onboarding Information">
                <dl className="divide-y divide-gray-200">
                  <DetailRow label="User ID" value={record.userId} />
                  <DetailRow label="User Name" value={record.userName} />
                  <DetailRow label="Surname" value={record.surName} />
                  <DetailRow
                    label="Phone Number"
                    value={record.phoneNumber}
                  />
                  <DetailRow
                    label="Unique Identifier (BVN / NIN)"
                    value={record.uniqueIdentifier}
                  />
                  <DetailRow label="Date of Birth" value={record.dateOfBirth} />
                  <DetailRow label="Reason" value={record.reason} />
                </dl>
              </SectionCard>
            </div>

            <div className="space-y-6">
              <SectionCard title="Status">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <TagIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-700">
                        Service Affected
                      </span>
                    </div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        serviceStyles[service] || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {record.serviceAffected || "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {status === "Failed" ? (
                        <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                      ) : status === "Completed" ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                      ) : (
                        <CheckCircleIcon className="h-5 w-5 text-gray-400 mr-2" />
                      )}
                      <span className="text-sm text-gray-700">
                        Admin Status
                      </span>
                    </div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        statusStyles[status] || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {record.adminStatus || "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-700">Created On</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {record.createdOn || "N/A"}
                    </span>
                  </div>
                </div>
              </SectionCard>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}